import React, {useState, useEffect} from 'react';
import {gql} from 'apollo-boost';
import {useMutation} from '@apollo/react-hooks';
import Button, {LoadingButton} from '../../Form/Button/Button';
import ButtonGroup from '../../Form/Button/ButtonGroup';
import Modal from '../../Modal/Modal';
import SmsOtp from '../../Session/SmsOtp';
import Select from '../../Form/Select/Select';
import TextAreaInput from '../../Form/TextInput/TextAreaInput';
import closureReasons from './closureReasons';

import './accountClosure.scss';
import successImg from "../../../assets/images/success.png";
import {useUnifiedAnalytics} from "../../../contexts/AnalyticsWrapper";

/** @import {ValueType} from 'react-select' */
/** @import {OptionType} from '../../Form/Select/SelectTypes' */
/** @import {NoticeAccountClosureModalProps, StepFourProps, StepOneProps, StepThreeProps, StepTwoProps} from './NoticeAccountClosureModalTypes' */
/** @import {DestinationAccount} from "../../MyAccount/MyAccountTypes" */
/** @import {VerifyOtpOnSuccess} from '../../Session/VerifyOtpTypes' */

const CLOSE_ACCOUNT = gql`
  mutation CloseNoticeAccount($request : CloseNoticeAccountRequestType!){
    closeNoticeAccount(request:$request) {
      success
    }
  }   
`;

const NoticeAccountClosureModal = (/** @type {NoticeAccountClosureModalProps} */ props) => {
    const [step, setStep] = useState(1);
    const [completionToken, setCompletionToken] = useState(/** @type {string | undefined} */ (''));
    const [closeAccount] = useMutation(CLOSE_ACCOUNT);
    const [selectedDestinationAccount, setSelectedDestinationAccount] = useState(/** @type {DestinationAccount | null} */(null));
    const {destinationAccounts} = props;
    
    useEffect(() => {
        if (props.selectedAccountNumber) {
            let selected = destinationAccounts.find(a => a.accountNumber === props.selectedAccountNumber);
            if (selected) {
                setSelectedDestinationAccount(selected);
            }
        }
    }, [props.selectedAccountNumber])

    const analytics = useUnifiedAnalytics();

    const onCancel = (/** @type {string} */ textClick, /** @type {string} */ action) => {
        if (textClick && action) {
            analytics.trackAnalyticsPopupClick(
                textClick,
                action,
                'closing ns account',
                'NS Account Closure',
                'account|onCancel|closing ns account'
            );

        }
        setStep(1);
        setSelectedDestinationAccount(null);
        if (props.onCancel) props.onCancel();
    }

    const onCloseSuccess = () => {
        setStep(1);
        setSelectedDestinationAccount(null);
        if (props.onClosed) props.onClosed();
    }

    /** @type {VerifyOtpOnSuccess} */
    const onOtpSuccess = (_e, _m, completionToken) => {
        setCompletionToken(completionToken);
        setStep(3);
    };

    const selectorAccounts = destinationAccounts.map(a => {
        return {
            label: a.label || '',
            value: a.accountNumber || ''
        }
    });

    const onSelectedAccountChanged = (/** @type {string} */ accountNumber) => {
        let selected = destinationAccounts.find(a => a.accountNumber === accountNumber);
        if (selected == null) return;
        setSelectedDestinationAccount(selected);
    };

    const onReasonEntered = async (/** @type {string} */ reason, /** @type {string} */ freeTextReason) => {
        if (!selectedDestinationAccount) return;

        const result = await closeAccount({
            variables: {
                request: {
                    accountId: props.accountId,
                    destinationSortCode: selectedDestinationAccount.sortCode,
                    destinationAccountNumber: selectedDestinationAccount.accountNumber,
                    completionToken: completionToken,
                    closureReason: reason,
                    additionalClosureReason: freeTextReason
                }
            }
        });

        return {
            success: result.data.closeNoticeAccount.success,
            callback: () => {
                setStep(4);
            }
        }
    };

    const renderStep = () => {
        if (step === 1)
            return <StepOne cancel={() => onCancel('Don\'t close my account','click')}
                            onProceed={() => { analytics.trackAnalyticsPopupClick(
                                    'Close my account',
                                    'click',
                                    'closing ns account',
                                    'Account Closure',
                                    'account|click|closing ns account'
                                );
                                setStep(2) } }
                            productName={props.productName}
                            selectorAccounts={selectorAccounts}
                            onSelectedAccountChanged={onSelectedAccountChanged}
                            selectedAccountNumber={props.selectedAccountNumber}
                            selectedDestinationAccount={selectedDestinationAccount}
            />
        if (step === 2)
            return <StepTwo mobileNumber={props.mobileNumber} onProceed={onOtpSuccess}/>
        if (step === 3)
            return <StepThree cancel={onCancel} onProceed={onReasonEntered}/>
        if (step === 4)
            return <StepFour close={onCloseSuccess}/>

        return <div></div>;
    };

    return (
        <Modal className="modal modal--fixed-height modal--wide inactivity-modal modal__mobile-optimised"
               onClose={step === 4 ? onCloseSuccess : () => onCancel('close','closed')} mobileOptimised>
            {renderStep()}
        </Modal>
    );
};

function StepOne(/** @type {StepOneProps} */ props) {
    return (
        <>
            <h2 className="modal__title">Closing your {props.productName}</h2>
            <p className="modal__slim">
                We're sorry you'd like to close your account.
            </p>
            <p className="modal__slim">
                If you choose to go ahead, you'll receive a final payment after 90 days, including your current balance and
                the interest you'll have earned by that date.
            </p>
            {!props.selectedAccountNumber && (
                <p className="modal__slim">
                    <b>Which account would you like us to make your final payment to?</b>
                    <Select
                        name='account'
                        placeholder='Select an account'
                        options={props.selectorAccounts}
                        onChange={option => option && props.onSelectedAccountChanged(option.value)}
                    />
                </p>
            )}
            <p className="modal__slim">
                You'll have access to your online dashboard for 12 months after closing your account. From here you'll
                be able to download your annual statements and interest certificates. We'll let you know when these are
                available.
            </p>
            <p className="modal__slim">
                To continue, select "Close my account" below and we'll text you a one-time password to confirm it's
                really you.
            </p>

            <div className="modal__action-group">
                <ButtonGroup>
                    <Button size='med' 
                        //@ts-expect-error not a prop - perhaps customClassNames?
                        className="account-closure__button" 
                        label='Close my account' onClick={props.onProceed}
                            disabled={!props.selectedDestinationAccount}/>
                    <Button variant='secondary' size='med' 
                        //@ts-expect-error not a prop - perhaps customClassNames?
                        className="account-closure__button" 
                        label="Don't close my account" onClick={props.cancel}/>
                </ButtonGroup>
            </div>
        </>
    );
};

function StepTwo(/** @type {StepTwoProps} */ props) {
    return (
        <>
            <h2 className="modal__title">Verify account closure</h2>
            <SmsOtp
                mobileNumber={props.mobileNumber}
                onSuccess={props.onProceed}
                analyticsRegion={"account details"}
                analyticsAction={"account closure"}
            />
        </>
    );
};

function StepThree(/** @type {StepThreeProps} */ props) {
    const [reason, setReason] = useState(/** @type {null | { value: ValueType<OptionType, false> | string; }} */ (null));
    const [freeTextReason, setFreeTextReason] = useState('');
    const [showSelectValidation, setShowSelectValidation] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);

    const freeTextLimit = 250;
    const analytics = useUnifiedAnalytics();

    const validate = () => {
        let valid = true;
        if (!reason) {
            valid = false;
            setShowSelectValidation(true);
        }

        // Free text is optional for both "unhappy_with_service" and "other"
        // No validation needed for optional fields

        return valid;
    };

    const proceed = async () => {
        setShowError(false);
        if (!validate() || !reason) {
            return;
        }

        setIsLoading(true);
        var result = await props.onProceed(
            //@ts-expect-error can be text or result of dropdown - need to see running code to validate if types are declared correctly (suspect slightly incorrect)
            reason.value, 
            freeTextReason);
        setIsLoading(false);

        if (!result?.success) {
            setShowError(true);
        } else {
            analytics.trackAnalyticsPopupClick(
                'Close confirm',
                'click',
                'closing ns account',
                'Account Closure',
                'confirmation|click|closing ns account'
            );

            result.callback();
        }
    };    
    
    const setFreeTextWithLimit = (/** @type {string} */ val) => {
        if (val.length <= freeTextLimit)
            setFreeTextReason(val);
    };

    /** @template T */
    const setAndClearValidation = (/** @type {T} */ val, /** @type {(val: T) => void} */ set) => {
        setShowError(false);
        setShowSelectValidation(false);
        set(val);
    };

    const renderNextButton = (/** @type {React.ReactNode} */ label, /** @type {boolean} */ isLoading, /** @type {boolean | undefined} */ disabled, /** @type {(() => void) | undefined} */ callback) => {
        return isLoading ?
            <LoadingButton size='med' customClassNames={['account-closure__button']}/> :
            <Button size='med' customClassNames={['account-closure__button']} disabled={disabled} label={label} onClick={callback}/>
    };

    return (
        <div className="closure__modal">
            <h2 className="modal__title">We're sorry you'd like to close your account</h2>
            <p className="modal__slim">
                Before closing your account, please let us know the reason. Every bit of feedback helps us to improve.
            </p>
            <p className="modal__slim modal__slim--extra-margin">                <Select
                    options={closureReasons}
                    placeholder='Please select a reason'
                    onChange={option => option && setAndClearValidation(
                        option, 
                        setReason)
                    }
                />
                <div className={showSelectValidation ? "login__error" : "login__error login__error--hide"}>
                    <span className="login__error--wrap">Please select a reason.</span>
                </div>
            </p>            {reason && reason.value === "unhappy_with_service" &&
            <p className="modal__slim modal__slim--extra-margin">
                <TextAreaInput
                    onChange={val => setAndClearValidation(val, setFreeTextWithLimit)}
                    placeholder="Please tell us more about what you were unhappy with"
                    value={freeTextReason}
                />
                <span
                    className="account-closure__char-count">{freeTextReason.length}/{freeTextLimit} characters used</span>
            </p>
            }

            {reason && reason.value === "other" &&
            <p className="modal__slim modal__slim--extra-margin">
                <TextAreaInput
                    onChange={val => setAndClearValidation(val, setFreeTextWithLimit)}
                    placeholder="Please specify your reason for closing the account"
                    value={freeTextReason}
                />
                <span
                    className="account-closure__char-count">{freeTextReason.length}/{freeTextLimit} characters used</span>
            </p>
            }

            <div className={showError ? "login__error" : "login__error login__error--hide"}>
                <p className="modal__slim">
                    <span className="login__error--wrap">Something went wrong, please try again.</span>
                </p>
            </div>            <div className="modal__action-group">
                <ButtonGroup>
                    {renderNextButton("Close account", isLoading, isLoading, () => { void proceed(); })}
                    <Button variant='secondary' size='med' 
                        //@ts-expect-error not a prop - perhaps customClassNames?
                        className="account-closure__button" 
                        label='Cancel' onClick={() => props.cancel('', '')}
                            disabled={isLoading}/>
                </ButtonGroup>
            </div>
        </div>
    );
};

function StepFour(/** @type {StepFourProps} */ props) {
    return (
        <>
            <div className="modal__spacer--small"/>
            <img src={successImg} className='account-activity__image' />
            <h4 className="modal__title-closure">Thank you</h4>
            <p className="modal__text-closure">Your account is scheduled to close.</p>
            <div className="modal__spacer--small"/>
            <Button size='med' customClassNames={["modal__button"]} label="OK" onClick={props.close}/>
        </>
    );
}

export default NoticeAccountClosureModal;